import React from "react";

import { AsyncSelect, IAsyncSelectProps } from "@amphore-dev/amphore-lib";
import { t } from "i18next";

import ProjectsService from "@/services/ProjectsService";
import ViewsService from "@/services/ViewsService";

import { renderSwatchOption } from "@/utils/USelect";
import { toWorkspaceOptions } from "@/utils/UWorkspaces";
import { cn } from "@/utils/cn";

import { TProject, TTaskProject } from "@/types";

/**
 * Options are full projects (`GET views/:id/projects`); the value may
 * be the lighter project a listed task carries (TTaskProject) — the select
 * only needs id, name and color from it.
 */
export interface IProjectSelectProps extends Partial<
	IAsyncSelectProps<TTaskProject>
> {
	/** The projects a view gathers... */
	viewId?: number;
	/** ...or a client's, whatever the views (`GET /projects?client=`). */
	clientId?: number;
	/** Leave archived projects out: nothing is written into them (a task form). */
	hideArchived?: boolean;
	/** Only the projects one may report on: the others' preview and save are refused (a report form). */
	reportable?: boolean;
}

export const ProjectSelect: React.FC<IProjectSelectProps> = ({
	viewId,
	clientId,
	hideArchived = false,
	reportable = false,
	...props
}) => {
	const fetchProjects = () => {
		// No scope at all: every project the person reaches, which is what
		// a report picked by project alone needs.
		const load = viewId
			? ViewsService.getViewProjects(viewId)
			: ProjectsService.list(clientId);
		// Across workspaces: grouped under each one's name once there are several.
		return load.then((projects) =>
			toWorkspaceOptions(
				projects.filter(
					(project: TProject) =>
						(!hideArchived || !project.isArchived) &&
						(!reportable || project.canReport)
				),
				(project: TProject) => ({
					value: project as TTaskProject,
					label: project.name,
				})
			)
		);
	};

	return (
		<AsyncSelect
			loadOptions={fetchProjects}
			isClearable={true}
			getOptionValue={(project) => project.id}
			renderOption={renderSwatchOption<TTaskProject>(true)}
			{...props}
			className={cn(["w-full", props.className])}
			placeholder={props.placeholder || t("projects.select")}
			label={props.label || t("projects.label")}
		/>
	);
};
